//
// Copyright (c) 2026 Comcast
//

import listenerMiddleware from 'listenerMiddleware'
import i18next from 'i18next'
import { navigate } from 'reducers/routing'

import { updateSessionUserParam } from 'api/sessionUser'
import * as sessionUserSelector from 'selectors/sessionUser'
import {
  addNotification,
  clearNotification,
  notificationButtonClick,
} from 'reducers/notification'
import { appIsLoaded } from 'reducers/session'

import { getGuessedTimezone, getGuessedTimezoneFull } from 'utils/time'
import { RouteProfile } from 'routes'

import {
  NOTIFICATION_TYPE_INFO,
  NOTIFICATION_TYPE_SUCCESS,
  NOTIFICATION_TZ_CHANGE_DETECTED,
  NOTIFICATION_TZ_CHANGE_APPROVED,
} from 'constants/notification'

// check timezone change on app loading and add notification
listenerMiddleware.startListening({
  actionCreator: appIsLoaded,
  effect: (action, listenerApi) => {
    const state = listenerApi.getState()

    const timezone = sessionUserSelector.timezone(state)
    const timezoneAutodetect = sessionUserSelector.timezoneAutodetect(state)
    const guessedTimezone = getGuessedTimezone()
    const guessedTimezoneFull = getGuessedTimezoneFull()

    if (timezoneAutodetect && guessedTimezone !== timezone) {
      listenerApi.dispatch(addNotification({
        text: i18next.t('commonNotifications.timezoneChangeDetected', { timezone: guessedTimezoneFull }),
        type: NOTIFICATION_TYPE_INFO,
        hideRefreshLink: true,
        isPersistent: true,
        notificationId: NOTIFICATION_TZ_CHANGE_DETECTED,
        actionButtons: [
          { text: i18next.t('common.yes'), id: 'yes', icon: 'icon-confirm-notification' },
          { text: i18next.t('common.no'), id: 'no', icon: 'icon-delete-notification' },
        ],
      }))
    }
  },
})

// on action button clicked update timezone or close notification
listenerMiddleware.startListening({
  actionCreator: notificationButtonClick,
  effect: async (action, listenerApi) => {
    const { notificationId, buttonId } = action.payload

    if (notificationId !== NOTIFICATION_TZ_CHANGE_DETECTED && notificationId !== NOTIFICATION_TZ_CHANGE_APPROVED) {
      return
    }

    const dispatch = listenerApi.dispatch
    const guessedTimezone = getGuessedTimezone()
    const guessedTimezoneFull = getGuessedTimezoneFull()

    if (notificationId === NOTIFICATION_TZ_CHANGE_DETECTED) {
      if (buttonId === 'no') {
        dispatch(updateSessionUserParam.initiate({ 'timezoneAutodetect': false }))
        dispatch(clearNotification())
      }

      if (buttonId === 'yes') {
        const response = await dispatch(updateSessionUserParam.initiate({ 'timezone': guessedTimezone })).unwrap()
        dispatch(clearNotification())
        const isError = response?.message?.type !== 'success'
        if (!isError) {
          dispatch(addNotification({
            text: i18next.t('commonNotifications.timezoneChangeApproved', { timezone: guessedTimezoneFull }),
            type: NOTIFICATION_TYPE_SUCCESS,
            hideRefreshLink: true,
            notificationId: NOTIFICATION_TZ_CHANGE_APPROVED,
            isPersistent: true,
            actionButtons: [
              { text: i18next.t('commonNotifications.updateProfile'), id: 'update', icon: 'icon-edit-16' },
            ],
          }))
        }
      }
    }

    if (notificationId === NOTIFICATION_TZ_CHANGE_APPROVED && buttonId === 'update') {
      dispatch(clearNotification())
      dispatch(navigate(RouteProfile()))
    }
  },
})
